import { API } from "./APIClient";
import qs from "qs";
import { getCurrentUserId } from "../utils/reduxUser";
import { downloadFile } from "../utils/downloadFile";

class KpiAPIClass {
    buildApiUrl(path) {
        const baseUrl = process.env.REACT_APP_API_URL;

        if (!baseUrl) return path;
        return `${baseUrl.replace(/\/$/, "")}${path}`;
    }

    formatLocalDate(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    mapKpiScheme(kpiScheme) {
        return {
            id: kpiScheme.KPI_scheme_id,
            categoryName: kpiScheme.category_name,
            completedRevenue: kpiScheme.completed_revenue,
            goalRevenue: kpiScheme.goal_revenue,
            kpiDate: new Date(kpiScheme.kpi_date),
        };
    }

    async fetchKpiScheme(cafeId) {
        const userId = getCurrentUserId();
        const response = await API.get(`/kpi/kpiSchemas/${cafeId}?employee_id=${userId}`);

        const result = [];
        for (const kpiScheme of response.data) {
            result.push(this.mapKpiScheme(kpiScheme));
        }

        return result;
    }

    async fetchAdminKpiScheme(cafeId) {
        const response = await API.get(`/admin/kpi/kpiSchemas/${cafeId}`);

        const result = [];
        for (const kpiScheme of response.data) {
            result.push(this.mapKpiScheme(kpiScheme));
        }

        return result;
    }

    async createKpiScheme(cafeId, { name, goal, date }) {
        const response = await API.post(`/sadmin/kpi/createKpiTask`, {
            category_name: name,
            completed_revenue: 0,
            goal_revenue: goal,
            cafe_id: cafeId,
            kpi_date: this.formatLocalDate(date),
        })

        return {
            id: response.data.KPI_scheme_id,
            categoryName: response.data.category_name,
            completedRevenue: response.data.completed_revenue,
            goalRevenue: response.data.goal_revenue,
            kpiDate: response.data.kpi_date,
        };
    }

    async deleteKpiScheme(cafeId, schemeId) {
        const query = qs.stringify({
            cafe_id: cafeId,
            kpi_id: schemeId,
        });

        const response = await API.delete(`/sadmin/kpi/deleteKpiTasks?${query}`);

        return response.data;
    }

    async updateKpiScheme(cafeId, schemeId, { name, goal, date, completed = 0 }) {
        const query = qs.stringify({
            kpi_id: schemeId,
        });

        const response = await API.post(`/sadmin/kpi/updateKpi?${query}`, {
            category_name: name,
            completed_revenue: completed,
            goal_revenue: goal,
            cafe_id: cafeId,
            kpi_date: this.formatLocalDate(date),
        });

        return {
            id: response.data.KPI_scheme_id,
            categoryName: response.data.category_name,
            completedRevenue: response.data.completed_revenue,
            goalRevenue: response.data.goal_revenue,
            kpiDate: response.data.kpi_date,
        };
    }

    async fetchKpiPlanTemplate(cafeId) {
        const response = await API.get(`/sadmin/kpi/kpiPlanTemplate?cafe_id=${cafeId}`, {
            responseType: "blob",
        });

        const contentDisposition = response.headers["content-disposition"] || "";
        const utfFilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const regularFilenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        const filename = utfFilenameMatch?.[1]
            ? decodeURIComponent(utfFilenameMatch[1])
            : regularFilenameMatch?.[1] || "kpi_plan_template.xlsx";

        return {
            file: response.data,
            filename,
        };
    }

    downloadKpiPlanTemplate(cafeId) {
        downloadFile(this.buildApiUrl(`/sadmin/kpi/kpiPlanTemplate?cafe_id=${cafeId}`), "kpi_plan_template.xlsx");
    }

    async batchCreateKpiTasks(cafeId, file) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await API.post(`/sadmin/kpi/batchCreateKpiTasks?cafe_id=${cafeId}`, formData);

        return {
            updated: response.data.updated,
            created: response.data.created,
            skipped: response.data.skipped,
        };
    }

    async deleteAllKpiSchemes(cafeId, month, year) {
        const query = qs.stringify({
            cafe_id: cafeId,
            month,
            year,
        });

        const response = await API.delete(`/sadmin/kpi/deleteAllKpiSchemes?${query}`);

        return response.data;
    }
}

const KpiAPI = new KpiAPIClass();
export default KpiAPI;
